import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/ambiente.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const AmbienteManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_ambiente: '',
    id_escola: '',
    codigo_tipo_ambiente: '',
    id_pavimento: '',
    nome_ambiente: '',
    area: '',
    perimetro: '',
    pe_direito: '',
    piso_area: '',
    id_tipo_piso: '',
    niveis: '',
    parede: '',
    teto_area: '',
    id_tipo_teto: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [tiposAmbiente, setTiposAmbiente] = useState([]);
  const [tipoAmbienteSearch, setTipoAmbienteSearch] = useState('');
  const [camposComErro, setCamposComErro] = useState([]);
  const [pavimentos, setPavimentos] = useState([]);
  const [pavimentoSearch, setPavimentoSearch] = useState('');
  const [escolas, setEscolas] = useState([]);
  const [escolaSearch, setEscolaSearch] = useState('');
  const [tiposPiso, setTiposPiso] = useState([]);
  const [pisoSearch, setPisoSearch] = useState('');
  const [tiposTeto, setTiposTeto] = useState([]);
  const [tetoSearch, setTetoSearch] = useState('');
  const [areasDiferentes, setAreasDiferentes] = useState(false);
  const [manterDados, setManterDados] = useState(false);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar tipos de ambiente com base na busca
  const tiposAmbienteFiltrados = useMemo(() => {
    if (!tipoAmbienteSearch) return tiposAmbiente;
    return tiposAmbiente.filter(tipo =>
      tipo.descr_tp_ambiente?.toLowerCase().includes(tipoAmbienteSearch.toLowerCase()) ||
      tipo.codigo_tipo_ambiente?.toString().includes(tipoAmbienteSearch)
    );
  }, [tiposAmbiente, tipoAmbienteSearch]);

  // Filtrar pavimentos com base na busca e escola selecionada
  const pavimentosFiltrados = useMemo(() => {
    // Primeiro filtrar por escola se houver uma selecionada
    let pavimentosDaEscola = pavimentos;
    if (formData.id_escola) {
      pavimentosDaEscola = pavimentos.filter(pav => pav.id_escola?.toString() === formData.id_escola?.toString());
    }

    // Se não houver busca, retornar pavimentos da escola (ou todos se nenhuma escola selecionada)
    if (!pavimentoSearch || pavimentoSearch.trim() === '') return pavimentosDaEscola;

    // Aplicar busca nos pavimentos filtrados
    const searchTerm = pavimentoSearch.toLowerCase().trim();
    return pavimentosDaEscola.filter(pav =>
      pav.id_pavimento?.toLowerCase().includes(searchTerm) ||
      pav.descr_pavimento?.toLowerCase().includes(searchTerm)
    );
  }, [pavimentos, pavimentoSearch, formData.id_escola]);

  // Filtrar escolas com base na busca
  const escolasFiltradas = useMemo(() => {
    if (!escolaSearch) return escolas;
    const searchTerm = escolaSearch.toLowerCase();
    return escolas.filter(escola =>
      escola.nome_escola?.toLowerCase().includes(searchTerm) ||
      escola.id_escola?.toString().includes(searchTerm)
    );
  }, [escolas, escolaSearch]);

  const pisosFiltrados = useMemo(() => {
    if (!pisoSearch) return tiposPiso;
    const searchTerm = pisoSearch.toLowerCase();
    return tiposPiso.filter(piso =>
      piso.descricao?.toLowerCase().includes(searchTerm)
    );
  }, [tiposPiso, pisoSearch]);

  const tetosFiltrados = useMemo(() => {
    if (!tetoSearch) return tiposTeto;
    const searchTerm = tetoSearch.toLowerCase();
    return tiposTeto.filter(teto =>
      teto.descricao?.toLowerCase().includes(searchTerm)
    );
  }, [tiposTeto, tetoSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  // Sincronizar campos de busca quando formData mudar (para edição)
  useEffect(() => {
    if (formData.id_tipo_piso && tiposPiso.length > 0) {
      const piso = tiposPiso.find(p => p.id === formData.id_tipo_piso);
      if (piso && pisoSearch !== piso.descricao) {
        setPisoSearch(piso.descricao);
      }
    }
    if (formData.id_tipo_teto && tiposTeto.length > 0) {
      const teto = tiposTeto.find(t => t.id === formData.id_tipo_teto);
      if (teto && tetoSearch !== teto.descricao) {
        setTetoSearch(teto.descricao);
      }
    }
  }, [formData.id_tipo_piso, formData.id_tipo_teto, tiposPiso, tiposTeto, pisoSearch, tetoSearch]);

  const fetchTiposAmbiente = useCallback(async () => {
    try {
      const response = await api.get('/tipo-ambiente');
      setTiposAmbiente(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de ambiente: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchPavimentos = useCallback(async () => {
    try {
      const response = await api.get('/pavimento');
      setPavimentos(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar pavimentos: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchEscolas = useCallback(async () => {
    try {
      const response = await api.get('/escola');
      setEscolas(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar escolas: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposPiso = useCallback(async () => {
    try {
      const response = await api.get('/tipo-material-piso');
      setTiposPiso(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de piso: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchTiposTeto = useCallback(async () => {
    try {
      const response = await api.get('/tipo-material-teto');
      setTiposTeto(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de teto: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/ambiente');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/ambiente');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  // ID é gerado automaticamente no backend ao clicar em Adicionar
  const fetchNextId = useCallback(() => {
    // Não busca mais o próximo ID - será gerado atomicamente no backend
  }, []);

  useEffect(() => {
    verificarAPI();
    fetchTiposAmbiente();
    fetchPavimentos();
    fetchEscolas();
    fetchTiposPiso();
    fetchTiposTeto();
    carregarDados();
    fetchNextId();
  }, [verificarAPI, fetchTiposAmbiente, fetchPavimentos, fetchEscolas, fetchTiposPiso, fetchTiposTeto, carregarDados, fetchNextId]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'ambiente', { id, dadosAtualizacao });

    try {
      const response = await api.put(`/ambiente/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'ambiente', response.data);
      if (response && response.data.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'ambiente', { id });

    try {
      const response = await api.delete(`/ambiente/${id}`);
      logarSucesso('Excluir', 'ambiente', response.data);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
      carregarDados(false);
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'ambiente');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const getColunas = useMemo(() => {
    const tiposAmbienteMap = tiposAmbiente.reduce((acc, tipo) => {
      acc[tipo.codigo_tipo_ambiente] = tipo.descr_tp_ambiente;
      return acc;
    }, {});

    const colunasBase = [
      {
        title: 'ID Ambiente',
        field: 'id_ambiente',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Escola',
        field: 'nome_escola',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar escola...',
        sorter: 'string'
      },
      {
        title: 'Tipo Ambiente',
        field: 'codigo_tipo_ambiente',
        width: 180,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar por tipo...',
        sorter: 'string',
        formatter: (cell) => {
          return tiposAmbienteMap[cell.getValue()] || cell.getValue();
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeTipo = tiposAmbienteMap[rowValue] || rowValue || '';
          return nomeTipo.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: {
          values: tiposAmbienteMap
        }
      },
      {
        title: 'ID Pavimento',
        field: 'id_pavimento',
        width: 180,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
      },
      {
        title: 'Nome',
        field: 'nome_ambiente',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        frozen: true,
        sorter: 'string',
      },
      {
        title: 'Área',
        field: 'area',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar área...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Perímetro',
        field: 'perimetro',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar perímetro...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Pé Direito',
        field: 'pe_direito',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar pé direito...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Área do Piso',
        field: 'piso_area',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar área...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Material do Piso',
        field: 'id_tipo_piso',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar material...',
        sorter: 'string',
        formatter: (cell) => {
          const id = cell.getValue();
          const piso = tiposPiso.find(p => p.id === id);
          return piso ? piso.descricao : '';
        },
      },
      {
        title: 'Níveis',
        field: 'niveis',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar níveis...',
        sorter: 'number',
      },
      {
        title: 'Parede',
        field: 'parede',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar parede...',
        sorter: 'number',
      },
      {
        title: 'Área do Teto',
        field: 'teto_area',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar área...',
        sorter: 'number',
        formatter: (cell) => {
          const valor = cell.getValue();
          return valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        },
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (rowValue === null || rowValue === undefined || rowValue === '') return false;

          const formattedValue = Number(rowValue).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const searchValue = headerValue.trim();

          if (formattedValue.includes(searchValue)) return true;

          const numericValue = rowValue.toString();
          if (numericValue.includes(searchValue.replace(',', '.'))) return true;

          const cleanFormatted = formattedValue.replace(/\./g, '').replace(',', '.');
          const cleanSearch = searchValue.replace(/\./g, '').replace(',', '.');

          return cleanFormatted.includes(cleanSearch);
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          const valorFormatado = valor != null ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.style.width = '100%';
          input.value = valorFormatado;

          onRendered(() => {
            input.focus();
            input.select();
          });

          input.addEventListener('blur', () => {
            let valor = input.value.replace(',', '.');
            success(parseFloat(valor) || null);
          });

          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              let valor = input.value.replace(',', '.');
              success(parseFloat(valor) || null);
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });

          return input;
        }
      },
      {
        title: 'Material do Teto',
        field: 'id_tipo_teto',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar material...',
        sorter: 'string',
        formatter: (cell) => {
          const id = cell.getValue();
          const teto = tiposTeto.find(t => t.id === id);
          return teto ? teto.descricao : '';
        },
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_ambiente, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_ambiente}"?`)) {
            excluirRegistro(data.id_ambiente);
          }
        },
        headerSort: false
      },
      ...colunasBase.map(col => {
        if (col.field === 'id_ambiente') {
          // ID Ambiente não é editável pois é a chave primária
          return col;
        }
        if (col.field === 'codigo_tipo_ambiente') {
          // Tipo Ambiente já tem editor 'list' definido
          return col;
        }
        // Editor para Material do Piso
        if (col.field === 'id_tipo_piso') {
          const pisoValues = tiposPiso.reduce((acc, piso) => {
            acc[piso.id] = piso.descricao;
            return acc;
          }, {});
          return { ...col, editor: 'list', editorParams: { values: pisoValues } };
        }
        // Editor para Material do Teto
        if (col.field === 'id_tipo_teto') {
          const tetoValues = tiposTeto.reduce((acc, teto) => {
            acc[teto.id] = teto.descricao;
            return acc;
          }, {});
          return { ...col, editor: 'list', editorParams: { values: tetoValues } };
        }
        // Campos numéricos já têm seus editores configurados
        if (['area', 'perimetro', 'pe_direito', 'piso_area', 'teto_area'].includes(col.field)) {
          return col;
        }
        // Demais campos são editáveis como texto
        return { ...col, editor: 'input' };
      })
    ];
  }, [tiposAmbiente, tiposPiso, tiposTeto, atualizarRegistro, excluirRegistro]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_ambiente'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(getColunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, getColunas, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { codigo_tipo_ambiente, id_pavimento, nome_ambiente, area, perimetro, pe_direito, piso_area, id_tipo_piso, niveis, parede, teto_area, id_tipo_teto } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!codigo_tipo_ambiente) {
      camposObrigatorios.push('Tipo Ambiente');
      erros.push('codigo_tipo_ambiente');
    }
    if (!id_pavimento) {
      camposObrigatorios.push('ID Pavimento');
      erros.push('id_pavimento');
    }
    if (!nome_ambiente) {
      camposObrigatorios.push('Nome');
      erros.push('nome_ambiente');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_ambiente: 'AUTO',
      codigo_tipo_ambiente,
      id_pavimento,
      nome_ambiente,
      area: area === '' ? null : area,
      perimetro: perimetro === '' ? null : perimetro,
      pe_direito: pe_direito === '' ? null : pe_direito,
      piso_area: areasDiferentes ? (piso_area === '' ? null : piso_area) : (area === '' ? null : area),
      id_tipo_piso: id_tipo_piso === '' ? null : id_tipo_piso,
      niveis: niveis === '' ? null : niveis,
      parede: parede === '' ? null : parede,
      teto_area: areasDiferentes ? (teto_area === '' ? null : teto_area) : (area === '' ? null : area),
      id_tipo_teto: id_tipo_teto === '' ? null : id_tipo_teto,
    };

    logarRequisicao('Adicionar', 'ambiente', dataToSend);

    try {
      setLoading(true);
      const response = await api.post('/ambiente', dataToSend);
      logarSucesso('Adicionar', 'ambiente', response.data);
      if (manterDados) {
        // Mantém todos os dados para facilitar cadastro sequencial, exceto ID
        setFormData(prev => ({
          ...prev,
          id_ambiente: ''
        }));
      } else {
        // Limpa tudo exceto Escola e Pavimento (pedido pelo usuário para sempre manter)
        setFormData(prev => ({
          id_ambiente: '',
          id_escola: prev.id_escola,
          id_pavimento: prev.id_pavimento,
          codigo_tipo_ambiente: '',
          nome_ambiente: '',
          area: '',
          perimetro: '',
          pe_direito: '',
          piso_area: '',
          id_tipo_piso: '',
          niveis: '',
          parede: '',
          teto_area: '',
          id_tipo_teto: ''
        }));
        setPisoSearch('');
        setTetoSearch('');
        setTipoAmbienteSearch('');
        setAreasDiferentes(false);
      }
      // Não limpar escolaSearch, pavimentoSearch pois id_escola/id_pavimento são mantidos sempre

      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      carregarDados(false);
      fetchNextId();
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'ambiente');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Remove o erro do campo quando ele for preenchido
    if (value) {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'ambientes.csv');
    }
  };

  const limparFormulario = (e) => {
    if (e) e.preventDefault();
    setFormData({
      id_ambiente: '',
      id_escola: '',
      codigo_tipo_ambiente: '',
      id_pavimento: '',
      nome_ambiente: '',
      area: '',
      perimetro: '',
      pe_direito: '',
      piso_area: '',
      id_tipo_piso: '',
      niveis: '',
      parede: '',
      teto_area: '',
      id_tipo_teto: ''
    });
    setEscolaSearch('');
    setPisoSearch('');
    setTetoSearch('');
    setPavimentoSearch('');
    setTipoAmbienteSearch('');
    setAreasDiferentes(false);
    setCamposComErro([]);
    mostrarMensagem('info', 'Formulário limpo.');
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Ambientes</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_ambiente</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/ambiente.mov" buttonText="Ver Tutorial Ambiente" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_ambiente">ID Ambiente:</label>
            <input
              type="text"
              id="id_ambiente"
              name="id_ambiente"
              value=""
              readOnly
              placeholder="Gerado ao adicionar"
              maxLength={255}
              disabled={true}
              style={{ backgroundColor: '#e9e9e9', cursor: 'not-allowed', color: '#888' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_escola">Escola (para filtrar pavimentos):<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_escola"
              name="id_escola"
              value={escolaSearch}
              onChange={(e) => {
                setEscolaSearch(e.target.value);
                const escolaEncontrada = escolas.find(escola =>
                  escola.nome_escola === e.target.value
                );
                if (escolaEncontrada) {
                  setFormData(prev => ({ ...prev, id_escola: escolaEncontrada.id_escola, id_pavimento: '' }));
                  setPavimentoSearch('');
                  setCamposComErro(prev => prev.filter(c => c !== 'id_escola'));
                } else {
                  setFormData(prev => ({ ...prev, id_escola: '', id_pavimento: '' }));
                  setPavimentoSearch('');
                }
              }}
              list="escolas-list-ambiente"
              placeholder="Digite para buscar escola..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_escola') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="escolas-list-ambiente">
              {escolasFiltradas.map(escola => (
                <option key={escola.id_escola} value={escola.nome_escola} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="id_pavimento">
              ID Pavimento:<span style={{ color: 'red', marginLeft: '2px' }}>*</span>
              {formData.id_escola && <small style={{ color: '#666', fontWeight: 'normal', marginLeft: '5px' }}>(filtrado pela escola)</small>}
            </label>
            <input
              type="text"
              id="id_pavimento"
              name="id_pavimento"
              value={pavimentoSearch}
              onChange={(e) => {
                setPavimentoSearch(e.target.value);
                const pavimentoEncontrado = pavimentos.find(pav =>
                  pav.id_pavimento === e.target.value ||
                  `${pav.id_pavimento} - ${pav.descr_pavimento || ''}`.trim() === e.target.value
                );
                if (pavimentoEncontrado) {
                  setFormData(prev => ({ ...prev, id_pavimento: pavimentoEncontrado.id_pavimento }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_pavimento'));
                } else {
                  setFormData(prev => ({ ...prev, id_pavimento: '' }));
                }
              }}
              list="pavimentos-list-ambiente"
              placeholder={formData.id_escola ? "Digite para buscar pavimento..." : "Selecione uma escola primeiro"}
              disabled={loading || apiStatus !== 'connected' || !formData.id_escola}
              className={camposComErro.includes('id_pavimento') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="pavimentos-list-ambiente">
              {pavimentosFiltrados.map(item => (
                <option key={item.id_pavimento} value={item.id_pavimento}>
                  {item.descr_pavimento || item.id_pavimento}
                </option>
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="codigo_tipo_ambiente">Tipo Ambiente:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="codigo_tipo_ambiente"
              name="codigo_tipo_ambiente"
              value={tipoAmbienteSearch}
              onChange={(e) => {
                setTipoAmbienteSearch(e.target.value);
                const tipoEncontrado = tiposAmbiente.find(tipo =>
                  tipo.descr_tp_ambiente === e.target.value
                );
                if (tipoEncontrado) {
                  setFormData(prev => ({ ...prev, codigo_tipo_ambiente: tipoEncontrado.codigo_tipo_ambiente }));
                  setCamposComErro(prev => prev.filter(c => c !== 'codigo_tipo_ambiente'));
                } else {
                  setFormData(prev => ({ ...prev, codigo_tipo_ambiente: '' }));
                }
              }}
              list="tipos-ambiente-list"
              placeholder="Digite para buscar tipo..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('codigo_tipo_ambiente') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="tipos-ambiente-list">
              {tiposAmbienteFiltrados.map(tipo => (
                <option key={tipo.codigo_tipo_ambiente} value={tipo.descr_tp_ambiente} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="nome_ambiente">Nome:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="nome_ambiente"
              name="nome_ambiente"
              value={formData.nome_ambiente}
              onChange={handleInputChange}
              placeholder="Ex: Sala de Aula"
              maxLength={100}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('nome_ambiente') ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="area">Área:</label>
            <input type="number" id="area" name="area" value={formData.area} onChange={handleInputChange} placeholder="Ex: 50,25" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>


          <div className="form-group">
            <label htmlFor="perimetro">Perímetro:</label>
            <input type="number" id="perimetro" name="perimetro" value={formData.perimetro} onChange={handleInputChange} placeholder="Ex: 30,5" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="pe_direito">Pé direito:</label>
            <input type="number" id="pe_direito" name="pe_direito" value={formData.pe_direito} onChange={handleInputChange} placeholder="Ex: 3,5" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_tipo_piso">Material do piso:</label>
            <input
              type="text"
              id="id_tipo_piso"
              name="id_tipo_piso"
              value={pisoSearch}
              onChange={(e) => {
                setPisoSearch(e.target.value);
                const pisoEncontrado = tiposPiso.find(piso =>
                  piso.descricao === e.target.value
                );
                if (pisoEncontrado) {
                  setFormData(prev => ({ ...prev, id_tipo_piso: pisoEncontrado.id }));
                } else {
                  setFormData(prev => ({ ...prev, id_tipo_piso: '' }));
                }
              }}
              list="tipos-piso-list"
              placeholder="Digite para buscar tipo de piso..."
              maxLength={100}
              disabled={loading || apiStatus !== 'connected'}
              autoComplete="off"
            />
            <datalist id="tipos-piso-list">
              {pisosFiltrados.map(piso => (
                <option key={piso.id} value={piso.descricao} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="niveis">Níveis:</label>
            <input type="number" id="niveis" name="niveis" value={formData.niveis} onChange={handleInputChange} placeholder="Ex: 1" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="parede">Parede:</label>
            <input type="number" id="parede" name="parede" value={formData.parede} onChange={handleInputChange} placeholder="Ex: 4" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_tipo_teto">Material do teto:</label>
            <input
              type="text"
              id="id_tipo_teto"
              name="id_tipo_teto"
              value={tetoSearch}
              onChange={(e) => {
                setTetoSearch(e.target.value);
                const tetoEncontrado = tiposTeto.find(teto =>
                  teto.descricao === e.target.value
                );
                if (tetoEncontrado) {
                  setFormData(prev => ({ ...prev, id_tipo_teto: tetoEncontrado.id }));
                } else {
                  setFormData(prev => ({ ...prev, id_tipo_teto: '' }));
                }
              }}
              list="tipos-teto-list"
              placeholder="Digite para buscar tipo de teto..."
              maxLength={255}
              disabled={loading || apiStatus !== 'connected'}
              autoComplete="off"
            />
            <datalist id="tipos-teto-list">
              {tetosFiltrados.map(teto => (
                <option key={teto.id} value={teto.descricao} />
              ))}
            </datalist>
          </div>

          {/* Linha de configurações extras e áreas específicas */}
          <div style={{ gridColumn: '1 / -1', marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '15px', display: 'flex', flexDirection: 'column', gap: '15px' }}>

            {/* Toggles */}
            <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label className="toggle-container" style={{ width: 'auto', marginBottom: 0 }}>
                <span className="toggle-text" style={{ marginRight: '10px', fontWeight: '500' }}>Definir áreas de teto e piso separadamente?</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={areasDiferentes}
                    onChange={(e) => setAreasDiferentes(e.target.checked)}
                    disabled={loading || apiStatus !== 'connected'}
                  />
                  <span className="slider"></span>
                </div>
              </label>

              <label className="toggle-container" style={{ width: 'auto', marginBottom: 0 }}>
                <span className="toggle-text" style={{ marginRight: '10px', fontWeight: '500' }}>Manter dados para o próximo cadastro?</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={manterDados}
                    onChange={(e) => setManterDados(e.target.checked)}
                  />
                  <span className="slider"></span>
                </div>
              </label>
            </div>

            {/* Inputs de Áreas Específicas (aparecem aqui se ativados) */}
            {areasDiferentes && (
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '5px' }}>
                <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                  <label htmlFor="teto_area">Área do teto:</label>
                  <input type="number" id="teto_area" name="teto_area" value={formData.teto_area} onChange={handleInputChange} placeholder="Ex: 50,25" step="0.01" disabled={loading || apiStatus !== 'connected'} style={{ width: '100%' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                  <label htmlFor="piso_area">Área do piso:</label>
                  <input type="number" id="piso_area" name="piso_area" value={formData.piso_area} onChange={handleInputChange} placeholder="Ex: 50,25" step="0.01" disabled={loading || apiStatus !== 'connected'} style={{ width: '100%' }} />
                </div>
              </div>
            )}
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
              <button className="btn btn-secondary" onClick={limparFormulario} type="button">🧹 Limpar Formulário</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default AmbienteManager;
